import { ApiResponse } from '@/types/services';
import {
  SalesExpert,
  UpdateExclusionsRequest,
  UserManagement,
  DeleteResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UserResponse
} from '@/types/user-management.types';

import { baseApi } from './base-api';

type GetUsersListResponse = ApiResponse<{
  users: UserManagement[];
}>;
type GetSalesExpertsListResponse = ApiResponse<{
  salesExperts: SalesExpert[];
}>;
type DeleteUserResponse = ApiResponse<DeleteResponse>;
type UpdateSalesExpertsExclusionsResponse = ApiResponse<{
  message: string;
}>;
type CreateUserResponse = ApiResponse<UserResponse>;
type UpdateUserResponse = ApiResponse<UserResponse>;

export const userManagementApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getUsersList: builder.query<UserManagement[], void>({
      query: () => ({ url: 'users' }),
      providesTags: ['UsersList'],
      transformResponse: (response: GetUsersListResponse): UserManagement[] => {
        return response?.data?.users || [];
      }
    }),
    getSalesExpertsList: builder.query<SalesExpert[], void>({
      query: () => ({ url: 'users/sales-experts-exclusions' }),
      providesTags: ['SalesExpertsList'],
      transformResponse: (response: GetSalesExpertsListResponse): SalesExpert[] => {
        return response?.data?.salesExperts || [];
      }
    }),
    deleteUser: builder.mutation<DeleteResponse, number>({
      query: id => ({ url: `users/${id}`, method: 'DELETE' }),
      transformResponse: (response: DeleteUserResponse): DeleteResponse => {
        return response?.data || ({} as DeleteResponse);
      },
      invalidatesTags: ['UsersList']
    }),
    createUser: builder.mutation<UserResponse, CreateUserRequest>({
      query: user => ({ url: 'users', method: 'POST', body: user }),
      transformResponse: (response: CreateUserResponse): UserResponse => {
        return response?.data || ({} as UserResponse);
      },
      invalidatesTags: ['UsersList']
    }),
    updateUser: builder.mutation<UserResponse, { id: number; data: UpdateUserRequest }>({
      query: ({ id, data }) => ({ url: `users/${id}`, method: 'PUT', body: data }),
      transformResponse: (response: UpdateUserResponse): UserResponse => {
        return response?.data || ({} as UserResponse);
      },
      invalidatesTags: ['UsersList']
    }),
    updateSalesExpertsExclusions: builder.mutation<string, UpdateExclusionsRequest>({
      query: request => ({
        url: 'users/sales-experts-exclusions',
        method: 'PUT',
        body: request
      }),
      transformResponse: (response: UpdateSalesExpertsExclusionsResponse): string => {
        return response?.data?.message || '';
      },
      async onQueryStarted({ exclusions }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          userManagementApi.util.updateQueryData('getSalesExpertsList', undefined, draft => {
            exclusions.forEach(update => {
              const expert = draft.find(e => e.expert_email === update.expert_email);
              if (expert) {
                const station = expert.stations.find(s => String(s.unicode) === String(update.excluded_station_unicode));
                if (station) {
                  station.active = update.active;
                }
              }
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ['SalesExpertsList']
    })
  })
});
export const {
  useGetUsersListQuery,
  useGetSalesExpertsListQuery,
  useDeleteUserMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useUpdateSalesExpertsExclusionsMutation
} = userManagementApi;
